import moment from "moment/min/moment-with-locales";
import React, { useMemo, useState } from "react";
import { Container } from "react-bootstrap";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { useFetchData } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import StationPicker from "../components/Pickers/StationPicker";
import { useUserFormState } from "../components/UserSelection";
import { DataContent, InputSelection, ModulePage, SearchWithDownloadRow, SoleDescriptionRow } from "../components/pageSections";
import PredictorHeader from "../components/predictor/PredictorHeader";
import PredictorRow from "../components/predictor/PredictorRow";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";

export default function Predictor() {
    const moduleName = "predictor";

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [filterData, setFilterData] = useState({ searchString: "" });

    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.station[0].radio_id);
        const url = `/playlist_autopilot/predictor/${formData.station[0].radio_id}?one_star_limit=5`;
        await fetchData(url);
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data.data;
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.song.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo, { key: "stars", direction: "descending" });

    const csvHeaders = ["Artist", "Title", "Airplay Market Rank", "Song Birth", "Market Spins", "Stars"];
    const csvData = useMemo(
        () => sortedItems?.map(x => [x.song.artist, x.song.title, x.airplay_market_rank, moment(x.song_birth).format("L HH:mm"), x.market_spins, x.stars]),
        [sortedItems]
    );

    return (
        <ModulePage>
            <ModulePage.Title>Predictor</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.station.length}>
                <InputSelection.Main>
                    <StationPicker
                        formData={formData}
                        setFormData={setFormData}
                        name={moduleName}
                        moduleFilterFunction={x => x.module_id === 16}
                        loadOnChange={loadFormData}
                    />
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Predictor"}
                        whatList={[
                            "Easy analysis and suggestions for new music for your station",
                            "Based on a portfolio of stations or markets you look to for inspiriation – any station in your country or elsewhere can be added",
                            "The “star” rating shows how successful the song is on other stations in your predictor market",
                        ]}
                        knowList={[
                            {
                                text: "Predictor compares what you have played and are playing to other hand picked stations, and looks for “new hits” on those stations you are missing out on",
                            },
                            { text: "Together with the “Hybrid Hitlist” function this covers almost all or your new music needs for most formats" },
                            { text: "Predictor runs daily" },
                        ]}
                        videoSrc="https://www.youtube.com/embed/YogIgjBZRLM?si=XQSUp75gQWO0vR00"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                <DataDescription.Station radioId={data.radio_id} />
                                <DataDescription.DateRange startDate={data.basis_week} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            csvHeaders={csvHeaders}
                            data={csvData}
                            filename={"Predictor"}
                        />
                        <PredictorHeader requestSort={requestSort} sortConfig={sortConfig} />
                        <Container fluid>
                            {sortedItems.map(item => (
                                <PredictorRow item={item} key={item.song.item_id} setCatalogueSong={setCatalogueSong} />
                            ))}
                            {!sortedItems?.length && <EmptyRow text="No songs found" />}
                        </Container>
                    </>
                )}
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
